import { Box, Stack, Typography } from "@mui/material";

import { rankColor } from "../helper";
import { GrowthIndicator } from "../common/growthindicator";


type RegionDistributionTooltipProps = {
  region: {
    rank: number;
    name: string;
    facilities: number;
    topProduct: string;
    growthPercentage: number | null;
    distribution: { label: string; value: number }[];
  };
};

const RegionDistributionTooltip = ({
  region,
}: RegionDistributionTooltipProps) => {
  const isPositive =
    region.growthPercentage !== null && region.growthPercentage > 0;

  const dotColor =
    region.growthPercentage === null || region.growthPercentage === 0
      ? "#94a3b8"
      : isPositive
        ? "#22c55e"
        : "#ef4444";

  return (
    <Box
      sx={{
        p: 2,
        minWidth: 300,
        background: "linear-gradient(180deg, #1b2434 0%, #1b2434 100%)",
        borderRadius: 2,
        color: "#fff",
        boxShadow: "0px 14px 40px rgba(0,0,0,0.55)",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start" mb={1.5}>

        <Box
          sx={{
            width: 20,
            height: 20,
            borderRadius: "50%",
            background: rankColor(region.rank),
            color: region.rank <= 2 ? "#000" : "#fff",
            fontWeight: 700,
            fontSize: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {region.rank}
        </Box>

        <Stack spacing={0.3}>
          <Typography fontWeight={700} fontSize={14} lineHeight={1.2}>
            {region.name}
          </Typography>

          <Stack direction="row" spacing={0.6} alignItems="center">
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                backgroundColor: dotColor,
                animation: "blinkPulse 1.4s infinite ease-in-out",
                "@keyframes blinkPulse": {
                  "0%": {
                    opacity: 1,
                    boxShadow: `0 0 4px ${dotColor}99`,
                  },
                  "50%": {
                    opacity: 0.35,
                    boxShadow: `0 0 10px ${dotColor}`,
                  },
                  "100%": {
                    opacity: 1,
                    boxShadow: `0 0 4px ${dotColor}99`,
                  },
                },
              }}
            />

            <GrowthIndicator value={region.growthPercentage} />
          </Stack>
        </Stack>
      </Stack>

      <Stack
        direction="row"
        spacing={2}
        mb={2}
        sx={{
          py: 1.5,
          borderTop: "1px solid rgba(255,255,255,0.12)",
          borderBottom: "1px solid rgba(255,255,255,0.12)",
        }}
      >
        <Box
          sx={{
            flex: 1,
            p: 1,
            borderRadius: 1.5,
            backgroundColor: "#242b35",
          }}
        >
          <Typography fontSize={11} color="#94a3b8">
            Facilities
          </Typography>
          <Typography fontWeight={700} color="#38bdf8">
            {region.facilities}
          </Typography>
        </Box>

        <Box
          sx={{
            flex: 1,
            p: 1,
            borderRadius: 1.5,
            backgroundColor: "#242b35",
          }}
        >
          <Typography fontSize={11} color="#94a3b8">
            Top Product
          </Typography>
          <Typography fontWeight={700} color="#0f69aa">
            {region.topProduct}
          </Typography>
        </Box>
      </Stack>

      <Typography fontSize={12} color="#94a3b8" mb={1}>
        Product Distribution
      </Typography>

      <Stack spacing={1}>
        {region.distribution.map((item) => (
          <Stack key={item.label} spacing={0.4}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography fontSize={12} sx={{ minWidth: 70 }}>
                {item.label}
              </Typography>

              <Box
                sx={{
                  flex: 1,
                  height: 6,
                  borderRadius: 999,
                  backgroundColor: "rgba(255,255,255,0.12)",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    height: "100%",
                    width: `${item.value}%`,
                    borderRadius: 999,
                    background: "linear-gradient(90deg, #0ea9d0, #38bdf8)",
                  }}
                />
              </Box>

              <Typography
                fontSize={12.5}
                fontWeight={600}
                sx={{ minWidth: 24, textAlign: "right" }}
              >
                {item.value}
              </Typography>
            </Stack>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

export default RegionDistributionTooltip;
